import { ApolloError } from '@apollo/client'
import { AdvancedApiError } from './client'

// No cause to name, so only the next step: "Failed to …" already says it went wrong.
const UNKNOWN_CAUSE = 'Try again; if it keeps happening, report it to the appropriate person.'
const SESSION_EXPIRED = 'Your session has expired. Reload the page to sign in again.'

// The code makes a coach's bug report name the check that refused it.
export const statusLabel = ({ status, code }: AdvancedApiError): string => (code ? `${status}, ${code}` : `${status}`)

// Apollo 3 turns every HTTP status of 300 or more into a network error, not only a missing response.
const serverResponseReason = (networkError: NonNullable<ApolloError['networkError']>): string => {
  const status = 'statusCode' in networkError ? networkError.statusCode : undefined
  if (status === undefined) {
    return "The server isn't responding. Check your connection and try again."
  }
  if (status === 401) {
    return SESSION_EXPIRED
  }
  if (status >= 502 && status <= 504) {
    return "The server isn't responding right now. Try again in a minute."
  }
  return status < 500 ? `The server refused the request (${status}). Reload and try again.` : UNKNOWN_CAUSE
}

// Never the raw message: `server` masks its own failures as "Unknown error", and a body may carry PHI.
export const failureReason = (error: unknown): string => {
  if (error instanceof ApolloError) {
    if (error.networkError) {
      return serverResponseReason(error.networkError)
    }
    return error.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN'
      ? "You don't have access to this patient."
      : UNKNOWN_CAUSE
  }
  if (error instanceof AdvancedApiError) {
    if (error.status === 401) {
      return SESSION_EXPIRED
    }
    // ADR-016: a 404 is "not your patient".
    if (error.status === 404) {
      return "You're not on this patient's Advanced care team."
    }
    if (error.status === 409) {
      return 'Someone else changed this first. Reload to see the latest.'
    }
    // The dev proxy and the load balancer answer these when the service itself can't be reached.
    if (error.status >= 502 && error.status <= 504) {
      return "Advanced isn't responding right now. Try again in a minute."
    }
    return error.status < 500
      ? `Advanced refused the request (${statusLabel(error)}). Reload and try again.`
      : UNKNOWN_CAUSE
  }
  // What `fetch` rejects with when there is no response at all.
  if (error instanceof TypeError) {
    return "Advanced isn't responding. Check your connection and try again."
  }
  return UNKNOWN_CAUSE
}

export type Failure = { summary: string; reason?: string }

// `server`'s coach-readable wording replaces the summary; any other failure adds its reason to it.
export const describeFailure = (summary: string, error: unknown): Failure => {
  const graphQLError = error instanceof ApolloError ? error.graphQLErrors[0] : undefined
  return graphQLError?.extensions?.clientError === true
    ? { summary: graphQLError.message }
    : { summary, reason: failureReason(error) }
}

export const failureText = ({ summary, reason }: Failure): string => (reason ? `${summary} ${reason}` : summary)
